import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {DndContext, DragOverlay, PointerSensor, closestCenter, useSensor, useSensors} from '@dnd-kit/core';
import {SortableContext} from '@dnd-kit/sortable';
import {PANEL_TYPES, dimensionNames, isTextPanel, textHasContent} from '../state/informeModel';
import {defaultTitleKey, panelSpan, rowsOf} from '../state/panelLayout';
import {panelTitle} from '../state/fieldLabels';
import {PANEL_STATE, isExplainedByNotice, panelRenderState} from '../state/panelRenderState';
import {consolidateWarnings} from '../state/renderWarnings';
import {eventDependentFieldsOf, panelUsesEventMetrics} from '../state/eventsCheck';
import {panelCaveats} from '../state/panelCaveats';
import {panelFilterNotes} from '../state/panelFilterNotes';
import {presetPanel} from '../state/presets';
import {useInforme} from '../state/InformeContext';
import {showUndoToast} from '../InformeToast';
import {AddPanelMenu} from './AddPanelMenu';
import {PanelControls} from './PanelControls';
import {PanelShell} from './PanelShell';
import {PanelEventsWarning} from './PanelEventsWarning';
import {PanelCaveatsWarning} from './PanelCaveatsWarning';
import {PanelFilterNotesWarning} from './PanelFilterNotesWarning';
import {SortablePanel} from './SortablePanel';
import {BarsBody, KpiBody, LineBody, PanelState, PieBody, TableBody} from './PanelBodies';
import {TextBody} from './text/TextBody';

const BODIES = {
    [PANEL_TYPES.KPI]: KpiBody,
    [PANEL_TYPES.BARS]: BarsBody,
    [PANEL_TYPES.PIE]: PieBody,
    [PANEL_TYPES.LINE]: LineBody,
    [PANEL_TYPES.TABLE]: TableBody,
};

/**
 * El folio: la grilla de 12 columnas con los paneles de la plantilla, en su orden, cada uno con
 * el `data` que el motor devolvió para su `id`. Editable (mockup de gráficos dinámicos):
 * arrastrar desde el grip (incluidas la tabla y las métricas destacadas), título con un clic,
 * ✕ con Deshacer, y los selects controlados de cada gráfico.
 *
 * `DragOverlay` liviano (título + tipo, sin recharts): arrastrar un panel con un
 * `ResponsiveContainer` adentro re-mide en cada frame.
 *
 * Dos cosas cambiaron con la card PlFYrmTo (07/09):
 *
 * 1. **La grilla no se deforma al arrastrar.** `rectSortingStrategy` transforma cada item
 *    asumiendo que todos miden lo mismo, y acá los spans son 3, 6 y 12: al arrastrar se movía la
 *    pantalla entera. Con `strategy={() => null}` los paneles quedan quietos y el destino se
 *    muestra con una barra de inserción al costado del panel apuntado, que es lo que hace que el
 *    drop sea predecible.
 * 2. **Agregar dejó de ser una tarjeta de la grilla.** Donde una fila tiene ancho libre aparece un
 *    «+ agregar acá» al pasar el mouse (el affordance OCUPA ese hueco, así que aparecer no corre
 *    nada), y al pie hay un botón que siempre crea una fila nueva.
 */
export function PanelsGrid({renderResult, fieldsData, loading, readOnly = false, panelRefsRef = null, onEditFields = null, reportFailed = false,
                            hasUnconfiguredEvents = false, onConfigureEvents = null, presets = null, sourceType = null,
                            onAddField = null}) {
    const {t} = useTranslation();
    const {state, actions} = useInforme();
    const panels = state.template.panels;
    const fields = state.template.fields;
    // El código que el aviso de arriba ya explica. Se deriva del MISMO `renderResult` que alimenta
    // a `RenderWarningsNotice`, así que el aviso y la degradación de los paneles no se pueden
    // desincronizar: si no hay aviso, no se degrada nada (review R-reports-fe-03).
    const noticeCode = useMemo(() => {
        const notice = consolidateWarnings(renderResult);
        return notice ? notice.code : null;
    }, [renderResult]);
    const [activeUid, setActiveUid] = useState(null);
    const [overUid, setOverUid] = useState(null);
    // El ancho REAL del bloque que se arrastra: el fantasma era una barra angosta que se salía del
    // folio por la derecha y no se parecía a lo que uno estaba moviendo (QA-VIS-12).
    const [ghostWidth, setGhostWidth] = useState(null);
    const refMap = useRef({});
    // Para el PDF: un ref por panel, en el orden del folio. En un efecto, no durante el render:
    // los refs se llenan en el commit (C-informes-53).
    //
    // Va con el `span` de cada bloque porque el PDF arma la MISMA grilla de 12 columnas: sin él,
    // el exportador dibujaba todo a ancho completo y cada métrica destacada se comía una franja
    // de hoja (card U3w64uUZ, 08/09). El span sale del modelo y no de medir la pantalla a
    // propósito: abajo de 992px el CSS colapsa la grilla a una columna, y el PDF de una laptop
    // angosta saldría apilado.
    useEffect(() => {
        if (!panelRefsRef) return;
        // El `type` va porque el exportador tiene que reconocer la tabla que `InformesPage` ya le
        // manda aparte como texto, para no rasterizarla además (salía DOS veces en el PDF).
        // Un bloque de texto vacío no va al PDF: sería una caja con el texto de ayuda del editor.
        panelRefsRef.current = panels
            .filter((p) => !isTextPanel(p) || textHasContent(p.content))
            .map((p) => ({current: refMap.current[p.uid] || null, span: panelSpan(p), type: p.type}));
    });

    const sensors = useSensors(useSensor(PointerSensor, {activationConstraint: {distance: 4}}));

    const byId = {};
    ((renderResult && renderResult.panels) || []).forEach((p) => { byId[p.id] = p; });

    const clearDrag = () => { setActiveUid(null); setOverUid(null); setGhostWidth(null); };

    const onDragEnd = ({active, over}) => {
        clearDrag();
        if (!over || active.id === over.id) return;
        const from = panels.findIndex((p) => p.uid === active.id);
        const to = panels.findIndex((p) => p.uid === over.id);
        if (from >= 0 && to >= 0) actions.movePanel(from, to);
    };

    // De qué lado del panel apuntado va a caer. Es exactamente lo que hará el splice de
    // `movePanel`, así que la barra no puede mentir sobre el resultado.
    const dropSideFor = (uid) => {
        if (!activeUid || !overUid || overUid !== uid || activeUid === overUid) return null;
        const from = panels.findIndex((p) => p.uid === activeUid);
        const to = panels.findIndex((p) => p.uid === overUid);
        if (from < 0 || to < 0) return null;
        return to > from ? 'after' : 'before';
    };

    const remove = (panel) => {
        const title = panel.title || t(defaultTitleKey(panel.type), panel.type);
        const entryId = actions.removePanel(panel.uid);
        showUndoToast(
            t('informes.panel.removed', {title, interpolation: {escapeValue: false}, defaultValue: `"${title}" quitado de la plantilla.`}),
            () => actions.undo(entryId),
            t('agent_chat.undo', 'Deshacer'),
            entryId,
        );
    };

    // `atEnd` decide qué dice el aviso, y no es un detalle: desde el «+ agregar aquí» el bloque
    // entra EXACTAMENTE en la fila que se apuntó, así que mandar a arrastrarlo sería describir un
    // problema que ya no existe. Desde el botón del pie sí va al final y ahí el consejo sirve.
    // Un solo toast para los altos: agregar cuatro paneles seguidos apilaba cuatro avisos que
    // tapaban la campana del topbar (QA UI-18).
    const announceAdded = (title, atEnd) => {
        const mensaje = atEnd
            ? t('informes.panel.added', {title, interpolation: {escapeValue: false},
                defaultValue: '"{{title}}" agregado al final. Arrástralo desde el ícono de puntos para ubicarlo.'})
            : t('informes.panel.added_here', {title, interpolation: {escapeValue: false},
                defaultValue: '"{{title}}" agregado en esa fila.'});
        toast.info(mensaje, {toastId: 'informes-panel-added', updateId: 'informes-panel-added'});
    };

    const addPanel = (type, index, {atEnd = false} = {}) => {
        actions.addPanel(type, index, fieldsData);
        announceAdded(t(defaultTitleKey(type), type), atEnd);
    };

    // Un sugerido nace con su título y sus columnas, que entran a la paleta (`presets.js`).
    const addPreset = (preset, index, {atEnd = false} = {}) => {
        const title = t(preset.nameKey, preset.name);
        const {panel, fields: presetFields} = presetPanel(preset, {fieldsData, sourceType, title});
        actions.addPresetPanel(panel, presetFields, index);
        announceAdded(title, atEnd);
    };

    // Sin dimensiones no hay eje para barras ni torta: se ofrecen deshabilitadas con el motivo,
    // porque nacerían sin dimensión, el backend las rechazaría y el selector del bloque quedaría
    // con cero opciones para arreglarlo (review C-editor-05).
    const sinDimensiones = !dimensionNames(fields).length;
    const disabledTypes = sinDimensiones ? {
        [PANEL_TYPES.BARS]: t('informes.panel.needs_dimension', 'Necesita una dimensión: agrega una en «Métricas y dimensiones».'),
        [PANEL_TYPES.PIE]: t('informes.panel.needs_dimension', 'Necesita una dimensión: agrega una en «Métricas y dimensiones».'),
    } : null;

    const activePanel = activeUid ? panels.find((p) => p.uid === activeUid) : null;
    // Cada métrica destacada lleva un color de borde distinto, en el orden en que aparecen
    // (mockup `KPI_COLORS`): el índice cuenta sólo KPI, así que quitar una no descoloca a las otras.
    const kpiOrder = panels.filter((p) => p.type === PANEL_TYPES.KPI).map((p) => p.uid);
    // El triángulo de «faltan eventos» (card UvIR4eHe): sólo en el editor y sólo en los bloques que
    // MUESTRAN una métrica de eventos. El cliente del portal no puede elegirlos.
    const eventFields = eventDependentFieldsOf(fieldsData);
    const warnEvents = !readOnly && hasUnconfiguredEvents && !!onConfigureEvents;

    // Un bloque de texto: sin controles ni avisos de datos, con su contenido editable en el lugar.
    const renderTextPanel = (panel) => (
        <SortablePanel key={panel.uid} panel={panel} disabled={readOnly} dropSide={dropSideFor(panel.uid)}>
            {(handle) => (
                <PanelShell
                    panel={panel}
                    state={PANEL_STATE.OK}
                    readOnly={readOnly}
                    dragHandleProps={handle}
                    fieldsData={fieldsData}
                    panelRef={(el) => { refMap.current[panel.uid] = el; }}
                    onRemove={() => remove(panel)}
                    onRename={(title) => actions.renamePanel(panel.uid, title)}
                >
                    <TextBody panel={panel} readOnly={readOnly}
                              onContentChange={(content) => actions.updatePanel(panel.uid, {content})}/>
                </PanelShell>
            )}
        </SortablePanel>
    );

    const renderPanel = (panel) => {
        if (isTextPanel(panel)) return renderTextPanel(panel);
        const Body = BODIES[panel.type];
        if (!Body) return null;
        const rendered = byId[panel.id] || byId[panel.uid] || null;
        const panelState = panelRenderState(rendered, {loading, hasResult: !!renderResult, reportFailed, noticeCode, panel});
        const explainedAbove = isExplainedByNotice(rendered, noticeCode);
        const opensTo = panel.type === PANEL_TYPES.KPI ? 'left' : 'right';
        // Las notas de filtro del nodo para ESTE bloque (tanda «Filtros que se respetan»): `null`
        // sin notas, y entonces nada de lo de abajo cambia.
        const filterNotes = panelFilterNotes(panel, rendered, fields, fieldsData, t);
        const headerWarnings = [
            warnEvents && panelUsesEventMetrics(panel, fields, eventFields)
                ? <PanelEventsWarning key="events" onConfigure={onConfigureEvents} opensTo={opensTo}/>
                : null,
            <PanelCaveatsWarning key="caveats" codes={panelCaveats(panel, rendered, fields, fieldsData)} opensTo={opensTo}/>,
            <PanelFilterNotesWarning key="filter-notes" filterNotes={filterNotes}/>,
        ];
        // Un bloque cuya métrica no aplica con los filtros se dibuja como «no aplica» aunque el motor
        // lo haya dejado VACÍO (sin la métrica en el summary): «Sin datos para este período» sería
        // la mentira que las notas vinieron a sacar.
        const showBody = panelState === PANEL_STATE.OK
            || (panelState === PANEL_STATE.EMPTY && !!filterNotes && filterNotes.allNotApplicable);
        return (
            <SortablePanel key={panel.uid} panel={panel} disabled={readOnly} dropSide={dropSideFor(panel.uid)}>
                {(handle) => (
                    <PanelShell
                        panel={panel}
                        state={panelState}
                        readOnly={readOnly}
                        dragHandleProps={handle}
                        fieldsData={fieldsData}
                        accentIndex={panel.type === PANEL_TYPES.KPI ? kpiOrder.indexOf(panel.uid) : null}
                        headerWarnings={headerWarnings}
                        panelRef={(el) => { refMap.current[panel.uid] = el; }}
                        onRemove={() => remove(panel)}
                        onRename={(title) => actions.renamePanel(panel.uid, title)}
                        controls={<PanelControls panel={panel} fields={fields} fieldsData={fieldsData} readOnly={readOnly}
                                                 tableTruncated={!!(rendered && rendered.data && rendered.data.truncated)}
                                                 onEditFields={onEditFields ? () => onEditFields(panel.uid) : null}
                                                 onAddField={onAddField && !readOnly ? (kind) => onAddField(panel.uid, kind) : null}
                                                 onChange={(patch) => actions.updatePanel(panel.uid, patch)}/>}
                    >
                        {/* El cuerpo se dibuja SÓLO con datos. Cualquier otro estado lo pinta
                            `PanelState`: antes se le pasaba `data={null}` y cada cuerpo caía por su
                            cuenta en «Sin datos para este período», que afirma algo sobre el
                            negocio del cliente cuando lo único cierto era que la consulta falló. */}
                        {showBody
                            ? <Body data={rendered ? rendered.data : null} fieldsData={fieldsData} panel={panel}
                                    filterNotes={filterNotes}/>
                            : <PanelState state={panelState} errorCode={rendered ? rendered.error_code : null}
                                          explainedAbove={explainedAbove}/>}
                    </PanelShell>
                )}
            </SortablePanel>
        );
    };

    // El «+ agregar aquí» de cada fila con lugar: ocupa EXACTAMENTE el hueco libre de esa fila, y
    // va INTERCALADO justo después del último panel de esa fila — en un grid la posición la da el
    // orden del DOM, así que todos juntos al final se apilarían abajo en vez de caer en su hueco.
    // Se esconde mientras se arrastra para no competir con el destino del drop.
    const showRowAdds = !readOnly && !activeUid;
    const addAfterIndex = {};
    if (showRowAdds) rowsOf(panels).filter((row) => row.free > 0).forEach((row) => { addAfterIndex[row.endIndex] = row.free; });

    const items = [];
    panels.forEach((panel, index) => {
        items.push(renderPanel(panel));
        const free = addAfterIndex[index];
        if (!free) return;
        items.push(
            <div key={`add-row-${panel.uid}`} className="inf-row-add" style={{gridColumn: `span ${free}`}}>
                <AddPanelMenu variant="row" label={t('informes.panel.add_here', 'agregar aquí')}
                              ariaLabel={t('informes.panel.add_after', {
                                  title: panelTitle(panel, fieldsData, t), interpolation: {escapeValue: false},
                                  defaultValue: 'Agregar un bloque después de «{{title}}»'})}
                              disabledTypes={disabledTypes}
                              presets={presets} onAddPreset={(preset) => addPreset(preset, index + 1)}
                              onAdd={(type) => addPanel(type, index + 1)}/>
            </div>
        );
    });

    const grid = (
        <div className={`inf-grid ${loading ? '-loading' : ''}`} data-testid="informes-grid">
            {items}
        </div>
    );
    if (readOnly) return grid;

    return (
        <>
            <DndContext sensors={sensors} collisionDetection={closestCenter}
                        onDragStart={({active}) => {
                            setActiveUid(active.id);
                            const r = active.rect && active.rect.current && active.rect.current.initial;
                            setGhostWidth(r ? r.width : null);
                        }}
                        onDragOver={({over}) => setOverUid(over ? over.id : null)}
                        onDragCancel={clearDrag}
                        onDragEnd={onDragEnd}>
                {/* Sin estrategia de sorting: ver el punto 1 del comentario de arriba. */}
                <SortableContext items={panels.map((p) => p.uid)} strategy={() => null}>
                    {grid}
                </SortableContext>
                <DragOverlay>
                    {activePanel && (
                        <div className="inf-drag-ghost"
                             style={{gridColumn: `span ${panelSpan(activePanel)}`, width: ghostWidth || undefined}}>
                            {activePanel.title || t(defaultTitleKey(activePanel.type), activePanel.type)}
                        </div>
                    )}
                </DragOverlay>
            </DndContext>
            <div className="inf-add-footer">
                <AddPanelMenu variant="footer" disabledTypes={disabledTypes}
                              presets={presets} onAddPreset={(preset) => addPreset(preset, panels.length, {atEnd: true})}
                              onAdd={(type) => addPanel(type, panels.length, {atEnd: true})}/>
            </div>
        </>
    );
}
